import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import TechPageBoundary from './TechPageBoundary';
import '../../pages/Booking.css';
import '../../booking-public-theme.css';

const scrollBookingTop = () => {
  if (typeof window === 'undefined') return;
  window.requestAnimationFrame(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  });
};

const BookingFlowBoundary = ({ children }) => {
  const location = useLocation();

  // Radix renders menus at document level. Mark the document while any public
  // booking form is mounted so those shared portal controls inherit this flow's
  // public palette instead of the authenticated application palette.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const roots = [document.documentElement, document.body].filter(Boolean);
    roots.forEach(root => root.classList.add('jvc-booking-flow-theme'));
    return () => roots.forEach(root => root.classList.remove('jvc-booking-flow-theme'));
  }, []);

  // Public booking routes must always start at the top rather than inheriting
  // the scroll position from the previous route/checkout screen.
  useEffect(() => {
    scrollBookingTop();
  }, [location.pathname, location.search]);

  // Booking.js changes steps without changing the URL. The progress dots are
  // the existing authoritative DOM representation of that step, so observe
  // only their data-state transition and reset scroll once the active step
  // changes. This avoids scroll jumps from slot loading or form state updates.
  useEffect(() => {
    if (typeof MutationObserver === 'undefined') return undefined;
    const root = document.querySelector('.nx-tech-page.nx-booking-flow');
    if (!root) return undefined;

    const activeStep = () => root.querySelector(".booking-progress-dot[data-state='active']")?.textContent || '';
    let previousStep = activeStep();

    const observer = new MutationObserver(() => {
      const nextStep = activeStep();
      if (!nextStep || nextStep === previousStep) return;
      previousStep = nextStep;
      scrollBookingTop();
    });

    observer.observe(root, {
      subtree: true,
      attributes: true,
      attributeFilter: ['data-state'],
    });

    return () => observer.disconnect();
  }, []);

  return (
    <TechPageBoundary pageClassName="nx-booking-flow">
      {children}
    </TechPageBoundary>
  );
};

export default BookingFlowBoundary;
